import type { RayHit, Entity, Player } from './types';

export class Renderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width: number;
  private height: number;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    this.ctx = ctx;
    this.width = canvas.width;
    this.height = canvas.height;
  }

  // Efface l'écran
  clear(): void {
    this.ctx.fillStyle = '#000';
    this.ctx.fillRect(0, 0, this.width, this.height);
  }

  // Dessine le ciel et le sol
  drawBackground(): void {
    // Ciel (gris foncé)
    this.ctx.fillStyle = '#1a1a2e';
    this.ctx.fillRect(0, 0, this.width, this.height / 2);

    // Sol (gris plus clair)
    this.ctx.fillStyle = '#0f3460';
    this.ctx.fillRect(0, this.height / 2, this.width, this.height / 2);
  }

  // Rendu des murs en raycasting
  renderWalls(rays: RayHit[], _screenWidth: number, screenHeight: number): void {
    for (let x = 0; x < rays.length; x++) {
      const ray = rays[x];
      
      if (ray.perpWallDist === 0) continue;

      // Hauteur du mur à l'écran
      const lineHeight = Math.floor(screenHeight / ray.perpWallDist);
      
      // Position de la ligne à dessiner
      const drawStart = Math.max(0, -lineHeight / 2 + screenHeight / 2);
      const drawEnd = Math.min(screenHeight, lineHeight / 2 + screenHeight / 2);

      // Couleur selon le type de mur
      let color = this.getWallColor(ray.wallType);
      
      // Ombre selon la distance
      const brightness = Math.max(0.2, 1 - ray.perpWallDist / 15);
      
      // Ombre selon le côté (vertical plus clair, horizontal plus sombre)
      const sideBrightness = ray.side === 0 ? 1.0 : 0.7;
      
      color = this.adjustBrightness(color, brightness * sideBrightness);

      // Dessiner la colonne verticale
      this.ctx.fillStyle = color;
      this.ctx.fillRect(x, drawStart, 1, drawEnd - drawStart);
    }
  }

  // Rendu des sprites (ennemis, items) avec z-buffer amélioré
  renderSprites(entities: Entity[], player: Player, rays: RayHit[]): void {
    // Créer un z-buffer à partir des rayons
    const zBuffer = rays.map(r => r.perpWallDist);

    // Trier les entités par distance (du plus loin au plus proche)
    const sortedEntities = entities
      .map(entity => {
        const dx = entity.x - player.x;
        const dy = entity.y - player.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        return { entity, distance, dx, dy };
      })
      .filter(item => item.entity.active !== false)
      .sort((a, b) => b.distance - a.distance);

    for (const item of sortedEntities) {
      const { entity, distance, dx, dy } = item;

      // Position relative au joueur
      const spriteX = dx;
      const spriteY = dy;

      // Transformation dans l'espace caméra
      const invDet = 1.0 / (player.planeX * player.dirY - player.dirX * player.planeY);
      const transformX = invDet * (player.dirY * spriteX - player.dirX * spriteY);
      const transformY = invDet * (-player.planeY * spriteX + player.planeX * spriteY);

      // Ne pas afficher si derrière la caméra
      if (transformY <= 0) continue;

      // Position à l'écran
      const spriteScreenX = Math.floor((this.width / 2) * (1 + transformX / transformY));

      // Taille du sprite
      const spriteHeight = Math.abs(Math.floor(this.height / transformY)) * 0.8;
      const spriteWidth = spriteHeight;

      const drawStartY = Math.max(0, -spriteHeight / 2 + this.height / 2);
      const drawEndY = Math.min(this.height, spriteHeight / 2 + this.height / 2);
      const drawStartX = Math.max(0, -spriteWidth / 2 + spriteScreenX);
      const drawEndX = Math.min(this.width, spriteWidth / 2 + spriteScreenX);

      // Z-buffer check pour chaque colonne du sprite
      for (let stripe = drawStartX; stripe < drawEndX; stripe++) {
        if (stripe < 0 || stripe >= zBuffer.length) continue;
        if (transformY < zBuffer[stripe]) {
          // Dessiner une colonne du sprite
          this.drawSpriteStripe(
            stripe,
            drawStartY,
            drawEndY,
            entity.type,
            distance,
            (stripe - drawStartX) / (drawEndX - drawStartX)
          );
        }
      }
    }
  }

  // Dessine une colonne verticale d'un sprite
  private drawSpriteStripe(x: number, startY: number, endY: number, type: string, distance: number, _texX: number): void {
    const brightness = Math.max(0.3, 1 - distance / 15);
    
    let color = '#ff0000'; // Rouge pour les ennemis
    if (type === 'item') color = '#00ff00';
    if (type === 'health') color = '#00ff00';
    if (type === 'ammo') color = '#ffff00';

    // Gradient simple pour donner du volume
    const gradient = this.ctx.createLinearGradient(x, startY, x, endY);
    const centerBrightness = brightness;
    const edgeBrightness = brightness * 0.6;
    
    gradient.addColorStop(0, this.adjustBrightness(color, edgeBrightness));
    gradient.addColorStop(0.5, this.adjustBrightness(color, centerBrightness));
    gradient.addColorStop(1, this.adjustBrightness(color, edgeBrightness));

    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(x, startY, 1, endY - startY);
  }

  // Retourne la couleur d'un type de mur
  private getWallColor(wallType: number): string {
    switch (wallType) {
      case 1: return '#8b4513'; // Marron
      case 2: return '#4a5568'; // Gris
      case 3: return '#2d3748'; // Gris foncé
      default: return '#000000';
    }
  }

  // Ajuste la luminosité d'une couleur
  private adjustBrightness(color: string, brightness: number): string {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    const newR = Math.floor(r * brightness);
    const newG = Math.floor(g * brightness);
    const newB = Math.floor(b * brightness);

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;
  }
}
